import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import './review.css';

// Distinct TEXT label + glyph per state (never colour-only) per spec Accessibility.
// Compact pill matching docs/design/specimen/review.html's `.appr` — a small,
// right-aligned pill — NOT a bulky contained MUI button (#342 review, #450 restyle).
const CONFIG = {
  pending: { label: 'Approve?', Icon: RadioButtonUncheckedIcon, title: 'Click: Approve', cls: '' },
  approved: { label: 'Approved', Icon: CheckCircleOutlineIcon, title: 'Click: Not approved', cls: 'ok' },
  stale: {
    label: 'Approved · stale', Icon: WarningAmberIcon,
    title: 'Content changed since approval — click to re-approve', cls: 'stale',
  },
  not_approved: { label: 'Not approved', Icon: CancelOutlinedIcon, title: 'Click: clear', cls: 'no' },
  // F-09 / REQ-6099 (#603, D-08): an approver holding authority signed off OVER
  // another reviewer's objection. Deliberately not rendered as a plain "Approved" —
  // the objection is retained on the record and a reader must be able to see the
  // sign-off was contested, which is the entire point of keeping both decisions.
  // The label carries that, so it is never colour-only (F-23 / REQ-6109).
  approved_over_objection: {
    label: 'Approved over objection', Icon: GavelOutlinedIcon,
    title: 'Approved over another reviewer’s objection — both decisions are retained',
    cls: 'ok objection',
  },
};

/**
 * Single approve pill — real `<button>`, cycles pending -> Approved -> Not
 * approved -> pending (one control, spec §Approve toggle). `aria-pressed` is
 * true whenever the item is in a non-neutral (decided) state. Visual states
 * (`.rmdv-appr[.ok|.no|.stale]`, review.css) are ported 1:1 from the mock's
 * `.appr` classes onto the same ds.css tokens the rest of the app uses.
 */
// F-11 / REQ-6101 (#603): staleness is SYMMETRIC — a rejection lapses exactly as an
// approval does. The `stale` pill therefore may not hard-code "Approved · stale"; that
// told a reviewer whose own decision was a REJECTION that they had approved the item.
// `staleState` is the caller's own lapsed decision, which ItemBlock resolves exactly
// (its `mineApprovals` holds a single row), so nothing is guessed on this surface.
const STALE_CONFIG = {
  approved: {
    label: 'Approved · stale',
    title: 'Content changed since your approval — click to re-approve',
  },
  not_approved: {
    label: 'Not approved · stale',
    title: 'Content changed since your rejection — it no longer blocks; click to re-approve',
  },
};

function ApprovePill({ state, onClick, staleState = null, busy = false }) {
  let cfg = CONFIG[state] || CONFIG.pending;
  if (state === 'stale' && STALE_CONFIG[staleState]) {
    cfg = { ...cfg, ...STALE_CONFIG[staleState] };
  }
  const { Icon } = cfg;
  // Owner ask, 2026-07-28: "The approval button needs to revert to a spinner immediately
  // on click while it's awaiting call response."
  //
  // The write is a network round trip to Supabase and can take a visible moment. With no
  // busy state the pill sat there still reading its OLD value with nothing happening, so
  // the natural response is to click it again — and this control is a CYCLE (pending ->
  // approved -> not approved -> pending), so a second click does not repeat the first: it
  // sends a DIFFERENT decision. Two clicks on a slow link could leave an item "not
  // approved" when the reviewer meant to approve it, with both writes on the approval
  // record. `disabled` closes that; the spinner is what makes the disable comprehensible
  // rather than a dead button.
  //
  // The LABEL stays. Swapping it for a bare spinner would drop the only text saying what
  // this control is (F-23 / REQ-6109 forbids carrying state on a non-text channel alone)
  // and would resize the pill mid-click, shifting the row under the pointer.
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={state !== 'pending'}
      aria-busy={busy || undefined}
      disabled={busy}
      onClick={onClick}
      title={busy ? 'Saving…' : cfg.title}
      data-testid="approve-pill"
      data-busy={busy ? 'true' : undefined}
      className={`rmdv-appr${cfg.cls ? ` ${cfg.cls}` : ''}${busy ? ' busy' : ''}`}
    >
      {busy
        ? <CircularProgress size={12} thickness={6} color="inherit" data-testid="approve-pill-spinner" />
        : <Icon sx={{ fontSize: 12 }} />}
      {cfg.label}
    </Box>
  );
}

ApprovePill.propTypes = {
  state: PropTypes.oneOf(['pending', 'approved', 'stale', 'not_approved',
    'approved_over_objection']).isRequired,
  onClick: PropTypes.func.isRequired,
  // F-11 / REQ-6101: which decision went stale, when `state === 'stale'`.
  staleState: PropTypes.oneOf(['approved', 'not_approved', 'mixed', null]),
  // True while this item's approval write is in flight: spinner + disabled.
  busy: PropTypes.bool,
};

export default ApprovePill;
